import {HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {
  FetchSideEffectsCommand,
  GetMoodsCommand,
  GetTenantProfileCommand,
  GetRolesCommand,
  FetchTenantConfigCommand,
  GetSideEffectSeveritiesCommand,
  GetChatCategoriesCommand,
  GetComorbidities,
} from './commands';
import {Moods, SideEffects, SideEffectSeverity} from './models';
import {InMemoryStorageService} from 'angular-webstorage-service';
import {cloneDeep} from 'lodash';
import {Observable, of} from 'rxjs';
import {tap, map} from 'rxjs/operators';

import {AnyAdapter} from '../api/adapters/any-adapter.service';
import {ApiService} from '../api/api.service';
import {ConfigKeys} from '../enums/config-keys.enum';
import {RolesAdapter} from './adapters/roles-adapter.service';
import {Role} from './models/role.model';
import {TenantConfig} from './models/tenant-config.model';
import {StoreKeys} from './store-keys.enum';
import {ChatCategory} from './models/chat-category.model';
import {TenantProfileAdapter} from '../adapters/tenant-profile-adapter.service';
import {TenantDetails} from '../models/tenant-details.model';
import { GetFeatureConfigCommand } from './commands/get-feature-config.command';
import { Features } from '../auth/models/features.model';
import { FeatureConfigAdapter } from '@drl/shared/adapters/feature-config-adapter.service';
import { GetCancerTypesWithIcdCodes } from './commands/get-cancer-types.command';
import { CancerTypes, CancerTypesWithIcdCodes } from './models/cancer-types-with-icd-codes.model';
import { GetCancerTypes } from './commands/get-only-cancer-types.command';
import { Comorbidities } from '@drl/shared/models';

@Injectable()
export class SystemStoreFacadeService {
  constructor(
    private readonly inMemoryStore: InMemoryStorageService,
    private readonly rolesAdapter: RolesAdapter,
    private readonly tenProfileAdapter: TenantProfileAdapter,
    private readonly anyAdapter: AnyAdapter,
    private readonly featuresAdapter: FeatureConfigAdapter,
    private readonly apiService: ApiService,
  ) {}

  getRoles(reset = false): Observable<Role[]> {
    const rolesInStore = this.inMemoryStore.get(StoreKeys.ROLES);
    if (!reset && rolesInStore) {
      return of(cloneDeep(rolesInStore));
    } else {
      const command: GetRolesCommand = new GetRolesCommand(
        this.apiService,
        this.rolesAdapter,
      );
      return command.execute().pipe(
        tap(roles => {
          this.inMemoryStore.set(StoreKeys.ROLES, cloneDeep(roles));
        }),
      );
    }
  }

  getChatCategories(reset = false): Observable<ChatCategory[]> {
    const chatCatsInStore = this.inMemoryStore.get(StoreKeys.CHAT_CATEGORIES);
    if (!reset && chatCatsInStore) {
      return of(cloneDeep(chatCatsInStore));
    } else {
      const command: GetChatCategoriesCommand<ChatCategory> =
        new GetChatCategoriesCommand(this.apiService, this.anyAdapter);
      return command.execute().pipe(
        tap(cats => {
          this.inMemoryStore.set(StoreKeys.CHAT_CATEGORIES, cloneDeep(cats));
        }),
      );
    }
  }

  getCancerTypesWithIcdCodes(reset = false): Observable<CancerTypesWithIcdCodes[]> | undefined {
    const cancerTypesInStore = this.inMemoryStore.get(StoreKeys.CANCER_TYPES_WITH_ICD_CODES);
    if (!reset && cancerTypesInStore) {
      return of(cloneDeep(cancerTypesInStore));
    } else {
      const command: GetCancerTypesWithIcdCodes<CancerTypesWithIcdCodes[]> =
        new GetCancerTypesWithIcdCodes(this.apiService, this.anyAdapter);

      return command.execute().pipe(
        tap(cancerTypes => {
          this.inMemoryStore.set(
            StoreKeys.CANCER_TYPES_WITH_ICD_CODES,
            cloneDeep(cancerTypes),
          );
        }),
      );
    }
  }

  getOnlyCancerTypes(reset = false): Observable<CancerTypes[]> | undefined {
    const cancerTypesInStore = this.inMemoryStore.get(StoreKeys.CANCER_TYPES);
    if (!reset && cancerTypesInStore) {
      return of(cloneDeep(cancerTypesInStore));
    } else {
      const command: GetCancerTypes<CancerTypes[]> =
        new GetCancerTypes(this.apiService, this.anyAdapter);

      return command.execute().pipe(
        tap(cancerTypes => {
          this.inMemoryStore.set(
            StoreKeys.CANCER_TYPES,
            cloneDeep(cancerTypes),
          );
        }),
      );
    }
  }

  getFoodCategories(reset = false): Observable<TenantConfig> | undefined {
    const catInStore = this.inMemoryStore.get(StoreKeys.FOOD_CAT);
    if (!reset && catInStore) {
      return of(cloneDeep(catInStore));
    } else {
      const command: FetchTenantConfigCommand<TenantConfig> =
        new FetchTenantConfigCommand(this.apiService, this.anyAdapter);

      command.parameters = {
        query: new HttpParams().set('config_key', ConfigKeys.FoodCategories),
      };

      return command.execute().pipe(
        tap(categories => {
          this.inMemoryStore.set(StoreKeys.FOOD_CAT, cloneDeep(categories));
        }),
      );
    }
  }

  getFoodBenefits(reset = false): Observable<TenantConfig> | undefined {
    const benefitsInStore = this.inMemoryStore.get(StoreKeys.FOOD_BENEFITS);
    if (!reset && benefitsInStore) {
      return of(cloneDeep(benefitsInStore));
    } else {
      const command: FetchTenantConfigCommand<TenantConfig> =
        new FetchTenantConfigCommand(this.apiService, this.anyAdapter);

      command.parameters = {
        query: new HttpParams().set('config_key', ConfigKeys.FoodBenefits),
      };

      return command.execute().pipe(
        tap(benefits => {
          this.inMemoryStore.set(StoreKeys.FOOD_BENEFITS, cloneDeep(benefits));
        }),
      );
    }
  }

  getFoodServingUnits(reset = false): Observable<TenantConfig> | undefined {
    const servingsunitsInStore = this.inMemoryStore.get(StoreKeys.FOOD_UNIT);
    if (!reset && servingsunitsInStore) {
      return of(cloneDeep(servingsunitsInStore));
    } else {
      const command: FetchTenantConfigCommand<TenantConfig> =
        new FetchTenantConfigCommand(this.apiService, this.anyAdapter);

      command.parameters = {
        query: new HttpParams().set('config_key', ConfigKeys.FoodServingUnits),
      };

      return command.execute().pipe(
        tap(unit => {
          this.inMemoryStore.set(StoreKeys.FOOD_UNIT, cloneDeep(unit));
        }),
      );
    }
  }

  getComorbidities(reset = false): Observable<Comorbidities[]> | undefined {
    const comorbiditiesInStore = this.inMemoryStore.get(
      StoreKeys.COMORBIDITIES,
    );
    if (!reset && comorbiditiesInStore) {
      return of(cloneDeep(comorbiditiesInStore));
    } else {
      const command: GetComorbidities<Comorbidities[]> =
        new GetComorbidities(this.apiService, this.anyAdapter);

      return command.execute().pipe(
        tap(comorbidities => {
          this.inMemoryStore.set(
            StoreKeys.COMORBIDITIES,
            cloneDeep(comorbidities),
          );
        }),
      );
    }
  }

  getSideEffects(reset = false): Observable<SideEffects[]> | undefined {
    const sideEffectsInStore = this.inMemoryStore.get(StoreKeys.SIDE_EFFECTS);
    if (!reset && sideEffectsInStore) {
      return of(cloneDeep(sideEffectsInStore));
    } else {
      const command: FetchSideEffectsCommand<SideEffects> =
        new FetchSideEffectsCommand(this.apiService, this.anyAdapter);

      const queryData = {
        name: true,
        id: true,
        alertKey: true
      };

      const queryStr = JSON.stringify({
        fields: queryData,
      });
      command.parameters = {
        query: new HttpParams().set('filter', queryStr),
      };

      return command.execute().pipe(
        tap(sideEffects => {
          this.inMemoryStore.set(
            StoreKeys.SIDE_EFFECTS,
            cloneDeep(sideEffects),
          );
        }),
      );
    }
  }

  getSideEffectSeverities(
    reset = false,
  ): Observable<SideEffectSeverity[]> | undefined {
    const sideEffectSeveritiesInStore = this.inMemoryStore.get(
      StoreKeys.SIDE_EFFECT_SEVERITY,
    );
    if (!reset && sideEffectSeveritiesInStore) {
      return of(cloneDeep(sideEffectSeveritiesInStore));
    } else {
      const command: GetSideEffectSeveritiesCommand<SideEffectSeverity> =
        new GetSideEffectSeveritiesCommand(this.apiService, this.anyAdapter);

      return command.execute().pipe(
        tap(sideEffectSeverity => {
          this.inMemoryStore.set(
            StoreKeys.SIDE_EFFECT_SEVERITY,
            cloneDeep(sideEffectSeverity),
          );
        }),
      );
    }
  }

  getMoods(reset = false): Observable<Moods[]> | undefined {
    const moodsInStore = this.inMemoryStore.get(StoreKeys.MOODS);
    if (!reset && moodsInStore) {
      return of(cloneDeep(moodsInStore));
    } else {
      const command: GetMoodsCommand<Moods> = new GetMoodsCommand(
        this.apiService,
        this.anyAdapter,
      );

      return command.execute().pipe(
        tap(moods => {
          this.inMemoryStore.set(StoreKeys.MOODS, cloneDeep(moods));
        }),
      );
    }
  }

  getTenantProfile(reset = false): Observable<TenantDetails> {

    const tenantProfileInStore = this.inMemoryStore.get(
      StoreKeys.TENANT_DETAILS,
    );

    if (!reset && tenantProfileInStore) {
      return of(cloneDeep(tenantProfileInStore));
    } else {
      const command: GetTenantProfileCommand<TenantDetails> =
        new GetTenantProfileCommand(this.apiService, this.tenProfileAdapter);


      return command.execute().pipe(
        map(tenDetails => {
          this.inMemoryStore.set(
            StoreKeys.TENANT_DETAILS,
            cloneDeep(tenDetails),
          );
          return tenDetails;
        }),
      );
    }
  }

  getLocale(reset = false): Observable<string> {
    return this.getTenantProfile(reset).pipe(map(details => details.locale));
  }

  // sonarignore:start
  setFilters(filterObj: any, filterRoute) {
    // sonarignore:end
    let inStore = this.inMemoryStore.get(StoreKeys.FILTERS);
    if (!inStore) {
      inStore = {};
    }
    if (!inStore[filterRoute]) {
      inStore[filterRoute] = {};
    }
    for (const prop in filterObj) {
      inStore[filterRoute][prop] = filterObj[prop];
    }

    this.inMemoryStore.set(StoreKeys.FILTERS, cloneDeep(inStore));
  }

  getFilters(filterRoute: string) {
    const filters = this.inMemoryStore.get(StoreKeys.FILTERS);
    return filters && filters[filterRoute];
  }

  getFeatureConfigs(): Observable<Features>{
    const featureConfigsInStore = this.inMemoryStore.get(
      StoreKeys.FeatureConfigs,
    );

    if (featureConfigsInStore) {
      return of(cloneDeep(featureConfigsInStore));
    } else {
      const command: GetFeatureConfigCommand<Features> =
        new GetFeatureConfigCommand(this.apiService, this.featuresAdapter);


      return command.execute().pipe(
        map(featureConfigs => {
          this.inMemoryStore.set(
            StoreKeys.FeatureConfigs,
            cloneDeep(featureConfigs),
          );
          return featureConfigs;
        }),
      );
    }
  }

  clearAll() {
    this.inMemoryStore.remove(StoreKeys.ROLES);
    this.inMemoryStore.remove(StoreKeys.FILTERS);
  }

  getRelations(reset = false): Observable<TenantConfig> | undefined {
    const catInStore = this.inMemoryStore.get(StoreKeys.RELATIONSHIPS);
    if (!reset && catInStore) {
      return of(cloneDeep(catInStore));
    } else {
      const command: FetchTenantConfigCommand<TenantConfig> =
        new FetchTenantConfigCommand(this.apiService, this.anyAdapter);

      command.parameters = {
        query: new HttpParams().set('config_key', ConfigKeys.Relations),
      };

      return command.execute().pipe(
        tap(categories => {
          this.inMemoryStore.set(StoreKeys.RELATIONSHIPS, cloneDeep(categories));
        }),
      );
    }
  }
}
